// Globaler Media-Apply-Jobs-Store. Hoert auf den "actions"-Channel
// und filtert auf kind="media.apply". Pro aktivem Job wird zusaetzlich
// der Per-Action-Live-Channel "files:media-apply:<id>" abonniert,
// damit wir die rsync-Markers (file N/T -- name) im Panel anzeigen
// koennen.
//
// Lifecycle: startMediaJobsStream() in App.svelte einmal beim Boot.
// Self-managed Subscriptions pro Job; bei settled/failed nach kurzer
// Anzeigedauer aus dem Map raus.

import { SvelteMap } from "svelte/reactivity";
import { registerRevalidator } from "./revalidate.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

export interface MediaJob {
  actionID: string;
  entityID: string;
  label: string;        // Quell-Folder-Name fuer Anzeige
  fileCount: number;
  // currentFile/totalFiles: aus dem ::HDFILE::K/T-Marker geparst.
  // currentFile zaehlt 1..totalFiles -- "wir starten gerade File K";
  // bereits *fertige* Files = currentFile - 1.
  currentFile: number;
  totalFiles: number;
  // withinFilePct: rsync's --info=progress2 % fuer das gerade laufende
  // File. 0..100. Wird kontinuierlich beim Lesen jeder rsync-
  // Statuszeile aktualisiert.
  withinFilePct: number;
  currentName: string;  // aktuell laufender Dateiname
  // Zusatz-Daten aus rsync's --info=progress2-Zeile fuer das aktuelle
  // File. bytesCur ist bisher uebertragen, speed ist die Live-Rate
  // (z.B. "31.99MB/s"), eta ist Restzeit (z.B. "0:01:23"). Leer bevor
  // rsync die erste Statuszeile geschickt hat.
  bytesCur: number;
  speed: string;
  eta: string;
  phase: "triggered" | "sent" | "settled" | "failed";
  reason: string;       // bei failed: Fehlertext
  startedAt: string;
}

const _jobs = new SvelteMap<string, MediaJob>();
// Per-Job WS-Subscriptions, damit wir bei settled/failed sauber
// abmelden -- sonst wuerde der Listener fuer Nicht-mehr-existente
// Action-IDs ewig im Hub haengen.
const _liveSubs = new Map<string, () => void>();
let _actionsUnsub: (() => void) | null = null;
let _started = false;

// Wie lange ein "fertig"/"fehler"-Job noch sichtbar bleibt bevor er
// aus dem Panel rausfliegt. Erfolg verschwindet schneller als Fehler,
// damit der User Zeit hat den Reason zu lesen.
const HOLD_SETTLED_MS = 4_000;
const HOLD_FAILED_MS = 12_000;

interface ActionFrame {
  id: string;
  kind: string;
  entity_id: string;
  phase: MediaJob["phase"];
  started_at: string;
  reason?: string;
  attributes?: { src_dir?: string; file_count?: number; label?: string };
}

function shortLabel(srcDir: string | undefined, fallback: string): string {
  if (!srcDir) return fallback;
  const parts = srcDir.split("/").filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : srcDir;
}

// channelForKind mappt einen tracker.kind auf den korrespondierenden
// per-Action-Live-Channel-Namen. Beide Backends (media.apply via SSH-
// Script, files.copy via SFTP) emittieren das gleiche HDFILE/log-
// Schema, nur die Channel-Namen unterscheiden sich.
function channelForKind(kind: string, id: string): string | null {
  if (kind === "media.apply") return `files:media-apply:${id}`;
  if (kind === "files.copy") return `files:copy:${id}`;
  return null;
}

function applyActionFrame(data: unknown): void {
  const a = data as ActionFrame | undefined;
  if (!a) return;
  const channel = channelForKind(a.kind, a.id);
  if (!channel) return;

  const existing = _jobs.get(a.id);
  const fileCount = Number(a.attributes?.file_count ?? 0);
  // Label-Praeferenz: explizite attributes.label > Source-Folder-Name >
  // entity_id als Notnagel. So kann das Backend pro Job einen schoenen
  // Anzeigetext mitschicken (z.B. "3 Einträge" oder "S01E09.mkv").
  const labelFromAttrs = typeof a.attributes?.label === "string" && a.attributes.label.length > 0
    ? a.attributes.label
    : shortLabel(a.attributes?.src_dir, a.entity_id);
  // currentFile/Name optimistisch aus attributes vorbelegen. Hintergrund:
  // das Backend publisht "file"-Events auf dem per-Action-Channel
  // unmittelbar nachdem die Triggered/Sent-Frames "actions" verlassen
  // haben -- der FE-Subscribe auf "files:copy:<id>" landet aber erst
  // nach einem WS-Roundtrip im Hub, sodass das *erste* file-Event
  // einer kurzen Datei verloren gehen kann. Mit den Defaults aus
  // file_count+label hat das Panel sofort eine sinnvolle Anzeige; das
  // tatsaechliche file-Event ueberschreibt sie spaeter bei Multi-File-
  // Jobs.
  const initialCurrentFile = fileCount > 0 ? 1 : 0;
  const job: MediaJob = existing ?? {
    actionID: a.id,
    entityID: a.entity_id,
    label: labelFromAttrs,
    fileCount,
    currentFile: initialCurrentFile,
    totalFiles: fileCount,
    withinFilePct: 0,
    currentName: labelFromAttrs,
    bytesCur: 0,
    speed: "",
    eta: "",
    phase: a.phase,
    reason: a.reason ?? "",
    startedAt: a.started_at,
  };
  job.phase = a.phase;
  if (a.reason) job.reason = a.reason;
  _jobs.set(a.id, job);

  // Live-Sub registrieren sobald wir den Job zum ersten Mal sehen.
  if (!_liveSubs.has(a.id)) {
    const off = wsSubscribe(channel, (msg) => onLive(a.id, msg));
    _liveSubs.set(a.id, off);
  }

  // Bei Terminal-Phase: nach kurzer Anzeigedauer aufraeumen.
  if (a.phase === "settled" || a.phase === "failed") {
    if (a.phase === "settled") {
      // Bei Erfolg explizit auf 100% setzen, damit der Ring sich nicht
      // bei einem letzten 99-irgendwas einfriert.
      job.currentFile = job.totalFiles;
      job.withinFilePct = 100;
    }
    _jobs.set(a.id, { ...job });
    const hold = a.phase === "settled" ? HOLD_SETTLED_MS : HOLD_FAILED_MS;
    setTimeout(() => {
      _jobs.delete(a.id);
      const off = _liveSubs.get(a.id);
      if (off) off();
      _liveSubs.delete(a.id);
    }, hold);
  }
}

// rsyncProgressRe matcht rsync's --info=progress2-Zeilen wie
//   "      33,587,200   1%   31.99MB/s    0:00:32"
// Capture-Groups: bytes, percent, speed (mit Einheit), eta (h:mm:ss).
const rsyncProgressRe =
  /^\s*([\d,]+)\s+(\d+)%\s+([\d.]+\s*[kMGT]?B\/s)\s+(\d+:\d{2}:\d{2})/;

function onLive(actionID: string, data: unknown): void {
  const f = data as { event?: string; progress?: string; name?: string; line?: string; error?: string } | undefined;
  if (!f?.event) return;
  const job = _jobs.get(actionID);
  if (!job) return;
  if (f.event === "file" && f.progress && f.name) {
    // "K/T" parsen. K = jetzt startendes File (1-indexed), T = total.
    const m = /^(\d+)\/(\d+)$/.exec(f.progress);
    if (m) {
      job.currentFile = parseInt(m[1], 10);
      job.totalFiles = parseInt(m[2], 10);
      job.withinFilePct = 0; // neuer File -> rsync faengt bei 0% an
      job.bytesCur = 0;
      job.speed = "";
      job.eta = "";
      job.currentName = f.name;
      _jobs.set(actionID, { ...job });
    }
    return;
  }
  if (f.event === "log" && typeof f.line === "string") {
    const pm = rsyncProgressRe.exec(f.line);
    if (pm) {
      const bytes = parseInt(pm[1].replace(/,/g, ""), 10);
      const pct = parseInt(pm[2], 10);
      const speed = pm[3].replace(/\s+/g, "");
      const eta = pm[4];
      if (!Number.isNaN(pct)) {
        job.bytesCur = Number.isNaN(bytes) ? job.bytesCur : bytes;
        job.withinFilePct = pct;
        job.speed = speed;
        job.eta = eta;
        _jobs.set(actionID, { ...job });
      }
    }
  }
}

// loadInitialSnapshot holt die Liste aller in-flight-Actions vom
// Backend und seedet das Panel daraus. Wichtig nach Page-Reload: WS-
// Frames bringen nur Future-State, der bereits laufende Job war im
// Stream-Backlog vor unserer Subscribe -- ohne Snapshot wuerde der
// Indikator leer aussehen, obwohl Backend-seitig alles weiterlaeuft.
async function loadInitialSnapshot(): Promise<void> {
  try {
    const r = await fetch("/v2-api/actions", { credentials: "include" });
    if (!r.ok) return;
    const arr = (await r.json()) as ActionFrame[];
    for (const a of arr) {
      if (channelForKind(a.kind, a.id)) {
        applyActionFrame(a);
      }
    }
  } catch {
    // Wenn der Snapshot scheitert, lebt der Stream weiter -- der erste
    // Live-Frame des Jobs (z.B. naechste Progress-Line) wuerde ihn
    // sonst auch wieder einklinken (allerdings ohne attribute-Defaults
    // weil der Erst-Frame schon durch war).
  }
}

export function startMediaJobsStream(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(loadInitialSnapshot);
  // Erst Subscribe (kein Roundtrip), dann Snapshot ziehen. Reihenfolge
  // wichtig damit ein Frame der zwischen Snapshot-Antwort und Subscribe-
  // Beginn anlaeuft nicht verloren geht (Subscribe ist schon offen).
  _actionsUnsub = wsSubscribe("actions", applyActionFrame);
  void loadInitialSnapshot();
}

export function stopMediaJobsStream(): void {
  _started = false;
  if (_actionsUnsub) {
    _actionsUnsub();
    _actionsUnsub = null;
  }
  for (const off of _liveSubs.values()) off();
  _liveSubs.clear();
  _jobs.clear();
}

// Reaktiver Snapshot fuer das Panel -- direkt die Map zurueckgeben,
// SvelteMap ist von $state-faehig.
export function mediaJobs(): SvelteMap<string, MediaJob> {
  return _jobs;
}
